import React, { useState, useEffect } from 'react'; // Import useEffect
import { useNavigate } from 'react-router-dom'; 
import { 
  Box, 
  Typography, 
  Button, 
  IconButton, 
  Alert, 
  CircularProgress, 
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  useTheme,
  alpha,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { User, UserRole } from '../../types/auth';
import { LoginSMAv2 } from './LoginSMAv2';
import { SubscriptionPlans } from './SubscriptionPlans';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../services/apiService';

interface RegistrationWorkflowProps {
  onComplete: (role: UserRole) => void;
  onClose: () => void;
  urlBase: string;
  onLogin: () => void;
}

export const RegistrationWorkflow: React.FC<RegistrationWorkflowProps> = ({
  onComplete,
  onClose,
  urlBase,
  onLogin // This might be less relevant now with BFF redirect handling
}) => {
  const [step, setStep] = useState(1);
  // const [user, setUser] = useState<User | null>(null); // User data now comes from useAuth
  const [selectedTier, setSelectedTier] = useState<UserRole | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate(); 
  
  // Get user and auth status from context
  const { user, isAuthenticated, token, sessionId, updateUser } = useAuth(); 

  // Effect to handle initial state based on authentication after redirect
  useEffect(() => {
    // If the component mounts and the user is already authenticated (e.g., after BFF redirect)
    // and we are still on step 1 or 2, move to step 3 (Choose Plan).
    if (isAuthenticated && user && (step === 1 || step === 2)) {
      console.log('RegistrationWorkflow: User authenticated on mount, moving to step 3.');
      setStep(3);
    } else if (!isAuthenticated && step === 3) {
      // If somehow user gets to step 3 but is not authenticated, go back to login step
      console.log('RegistrationWorkflow: User not authenticated on step 3, moving back to step 2.');
      setStep(2);
    }
    // Only run on mount or when auth status changes while modal is open
  }, [isAuthenticated, user, step]); 

  const handlePlanSelect = async (role: UserRole) => {
    try {
      setSelectedTier(role);
      setLoading(true);
      setError(null);
      
      // Use user from context
      if (!token || !user?.loginId) { 
        throw new Error('Authentication details not found');
      }

      // Use the centralized apiRequest instead of direct fetch
      const data = await apiRequest('/auth/add_member', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          twitter_handle: user.loginId, // Use user from context
          name: user.userName, // Use user from context
          role: role
        })
      });
      console.log('DEBUG: RegistrationWorkflow: API response:', data);
      
      // Handle paid plans that require checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        // navigate(data.checkout_url); // Redirect to checkout URL
        return; // Exit early
        // const popup = window.open(data.checkout_url, 'Stripe Checkout', 'width=600,height=800');
        // if (!popup) {
        //   throw new Error('Popup blocked. Please allow popups for this site.');
        // }
        
        // // Wait for payment completion via message event
        // await new Promise<void>((resolve, reject) => {
        //   const handleMessage = (event: MessageEvent) => {
        //     if (event.data.type === 'PAYMENT_SUCCESS') {
        //       window.removeEventListener('message', handleMessage);
        //       popup.close();
        //       resolve();
        //     } else if (event.data.type === 'PAYMENT_ERROR' || event.data.type === 'PAYMENT_CANCELLED') {
        //       window.removeEventListener('message', handleMessage);
        //       popup.close();
        //       reject(new Error(event.data.error || 'Checkout failed'));
        //     }
        //   };
        //   window.addEventListener('message', handleMessage);
          
        //   // Set a timeout to handle cases where no message is received
        //   setTimeout(() => {
        //     window.removeEventListener('message', handleMessage);
        //     if (popup && !popup.closed) {
        //       popup.close();
        //     }
        //     reject(new Error('Payment confirmation timed out. Please check your account status.'));
        //   }, 300000); // 5 minute timeout
        // });
      }

      // console.log('DEBUG: RegistrationWorkflow: Payment completed, verifying token...');
      // Verify token and get updated user data - use apiRequest
      const verifyData = await apiRequest('?verifyToken', {
        requireAuth: true
      });
      
      if (!verifyData.valid || !verifyData.payload) {
        throw new Error('Invalid token or user data');
      }
      
      // Update user data with new role
      updateUser(verifyData.payload);
      
      // Complete registration
      onComplete(role);
      // Navigate after completion (e.g., to dashboard or home)
      navigate('/'); // Or navigate('/') or based on role
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: "Welcome",
      content: (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom fontWeight="600">
            Welcome to SMA Insights
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 4, 
              maxWidth: '600px', 
              mx: 'auto',
              color: 'text.secondary'
            }}
          >
            Get access to powerful market insights and analytics to help you make better investment decisions. Our platform provides institutional-grade market intelligence for individual investors.
          </Typography>
          <Button 
            variant="contained" 
            color="primary"
            size="large"
            onClick={() => setStep(2)}
            sx={{ 
              py: 1.5,
              px: 4,
              borderRadius: 2,
              fontWeight: 600,
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.25)}`,
              '&:hover': {
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
              }
            }}
          >
            Get Started
          </Button>
        </Box>
      )
    },
    {
      title: "Sign In",
      content: (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom fontWeight="600">
            Sign In to Continue
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 4,
              color: 'text.secondary',
              maxWidth: '500px',
              mx: 'auto'
            }}
          >
            Please sign in with your preferred account to access our analytics platform
          </Typography>
          <LoginSMAv2 
            urlBase={urlBase}
            loginPath='register' // Pass the context
            setError={setError}
            // onLoginSuccess is no longer needed here as auth state is handled globally
            // onLoginSuccess={(userData) => {
            //   // setUser(userData); // User state now managed by AuthContext
            //   setStep(3); 
            // }}
          />
        </Box>
      )
    },
    {
      title: "Choose Plan",
      content: (
        <Box>
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 4,
                maxWidth: '800px',
                mx: 'auto'
              }} 
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
              <CircularProgress size={48} />
            </Box>
          ) : (
            <SubscriptionPlans
              onSelect={handlePlanSelect}
              currentRole="unregistered"
            />
          )}
        </Box>
      )
    }
  ];

  // Render null or loading if we are in step 1/2 but waiting for auth check
  if ((step === 1 || step === 2) && isAuthenticated === undefined) {
     return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>; // Or some loading state
  }

  return (
    <Box sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      bgcolor: alpha(theme.palette.background.default, 0.98),
      zIndex: 1300,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'auto',
      p: { xs: 2, md: 0 }
    }}>
      <Container maxWidth="lg">
        <Card
          elevation={6}
          sx={{
            position: 'relative',
            maxWidth: '1000px',
            width: '100%',
            mx: 'auto',
            borderRadius: 3,
            overflow: 'visible',
            mb: 4,
            boxShadow: `0 10px 40px ${alpha(theme.palette.common.black, 0.1)}`
          }}
        >
          <Box 
            sx={{ 
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              p: 3,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              Account Registration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Complete these steps to create your account
            </Typography>
          </Box>
          
          <IconButton
            onClick={() => {
              onClose();
              // Optionally navigate on close if needed
              // navigate('/'); 
            }}
            sx={{ 
              position: 'absolute', 
              right: 16, 
              top: 16,
              color: 'text.secondary'
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Stepper component */}
          <Box sx={{ px: 4, py: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Stepper 
              activeStep={step - 1} 
              alternativeLabel
              sx={{ 
                '& .MuiStepConnector-line': {
                  borderColor: alpha(theme.palette.divider, 0.3)
                },
                '& .MuiStepLabel-labelContainer': {
                  mt: 1
                }
              }}
            >
              {steps.map((s, i) => (
                <Step key={i} completed={step > i + 1}>
                  <StepLabel>{s.title}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <CardContent sx={{ p: 4 }}>
            {steps[step - 1].content}
          </CardContent>
        </Card>
        
        <Box sx={{ textAlign: 'center', mt: 2, mb: 4 }}>
          <Button 
            variant="text"
            color="inherit"
            onClick={() => {
              onClose();
              // Optionally navigate on close if needed
              // navigate('/'); 
            }}
            sx={{ 
              fontSize: '0.875rem',
              opacity: 0.7,
              '&:hover': {
                opacity: 1
              }
            }}
          >
            Return to Homepage
          </Button>
        </Box>
      </Container>
    </Box>
  );
};